import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useCollaborator } from "../../../hooks/collaborators/useCollaborator";
import {
  useCSVCollaboratorData,
  CSVCollaborator,
} from "../../../hooks/CSV/collaborators/useCSVCollaboratorData";
import { useImportHistory } from "../../../hooks/CSV/collaborators/useImportHistoryCollaborators";
import { importHistoryCollaboratorAPI } from "../../../hooks/importHistory/importHistoryCollaboratorAPI";
import { ImportHistoryTable } from "../list/CSV/ImportHistoryTable";
import { CSVCollaboratorPreviewTable } from "../list/CSV/CSVCollaboratorPreviewTable";
import { InvalidDataTable } from "../list/CSV/InvalidDataTable";
import ErrorDetailsModal from "../list/CSV/ErrorDetailsModal";
import { CSVCollaboratorProcessingError } from "../../../utils/csv/csv-validatorCollaborator";

type CSVCollaboratorManagementViewProps = {
  token: string;
  onClose: () => void;
  refetch: () => void;
};

type ErrorDetail = {
  collaborator: CSVCollaborator;
  error: string;
  is_restore?: boolean;
  restored?: boolean;
  id: string;
};

export function CSVCollaboratorManagementView({
  token,
  onClose,
  refetch,
}: CSVCollaboratorManagementViewProps) {
  const {
    selectedFile,
    setSelectedFile,
    CSVCollaboratorError,
    CSVCollaboratorPreviewData,
    invalidData,
    fileInputKey,
    setFileInputKey,
    validateCollaborator,
    handleCSVCollaboratorFileUpload,
    setCSVCollaboratorPreviewData,
    setInvalidData,
  } = useCSVCollaboratorData();

  const {
    importHistory,
    isFetchingHistory,
    fetchImportHistory,
    setImportHistory,
  } = useImportHistory(token);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingCollaborator, setEditingCollaborator] = useState<CSVCollaborator | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetail[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  const {
    processCSVCollaborator,
    isProcessingCSVCollaborator,
    CSVCollaboratorError: processCSVCollaboratorError,
  } = useCollaborator(token);

  useEffect(() => {
    fetchImportHistory();
  }, [fetchImportHistory]);

  const handleSaveImportError = useCallback(async () => {
    const errorImportStatusData = {
      file_name: selectedFile?.name || "Desconhecido",
      timestamp: new Date().toISOString(),
      status: "finished" as const,
      progress: 0,
      table_name: "collaborators",
      total: CSVCollaboratorPreviewData.length,
      success: 0,
      detailed_errors: [] as {
        id: string;
        collaborator: CSVCollaborator;
        error: string;
        is_restore?: boolean;
        restored?: boolean;
      }[],
      error: "Erro no processamento",
    };

    try {
      const errorImportStatus = await importHistoryCollaboratorAPI.create(
        errorImportStatusData,
        token
      );
      setImportHistory((prev: any) => [errorImportStatus, ...prev]);
    } catch (saveError) {
      console.error(
        "Erro ao salvar histórico de importação com falha:",
        saveError
      );
    }
  }, [selectedFile, CSVCollaboratorPreviewData.length, token, setImportHistory]);

  const handleProcessImportInBackground = useCallback(async () => {
    if (!selectedFile || CSVCollaboratorPreviewData.length === 0) return;

    try {
      const result = await processCSVCollaborator(CSVCollaboratorPreviewData);

      const newImportStatusData = {
        file_name: selectedFile.name,
        timestamp: new Date().toISOString(),
        status: "finished" as const,
        progress: result.success,
        total: CSVCollaboratorPreviewData.length,
        table_name: "collaborators",
        success: result.success,
        detailed_errors: result.errors.map(
          (error: CSVCollaboratorProcessingError) => ({
            id: error.id,
            collaborator: error.collaborator,
            error: error.error,
            is_restore: error.collaborator.is_restore,
            restored: false,
          })
        ),
        error:
          result.errors.length > 0
            ? `${result.errors.length} erro(s) ao importar`
            : undefined,
      };

      const newImportStatus = await importHistoryCollaboratorAPI.create(
        newImportStatusData,
        token
      );
      setImportHistory((prev: any) => [newImportStatus, ...prev]);

      setSelectedFile(null);
      setCSVCollaboratorPreviewData([]);
      setInvalidData([]);
      setFileInputKey(Date.now());

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        onClose();
        refetch();
      }, 3500);
    } catch (error) {
      console.error("Erro ao processar importação:", error);
      setProcessError("Erro ao processar importação.");
      handleSaveImportError();
    }
  }, [
    selectedFile,
    CSVCollaboratorPreviewData,
    processCSVCollaborator,
    token,
    refetch,
    handleSaveImportError,
    setImportHistory,
    setSelectedFile,
    setCSVCollaboratorPreviewData,
    setInvalidData,
    setFileInputKey,
    onClose,
  ]);

  const handleCollaboratorRestored = useCallback((id: string) => {
    setErrorDetails((prevErrorDetails) =>
      prevErrorDetails.map((errorDetail) =>
        errorDetail.id === id
          ? { ...errorDetail, restored: true, is_restore: false }
          : errorDetail
      )
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, []);

  const handleRemovePreviewItem = useCallback(
    (index: number) => {
      setCSVCollaboratorPreviewData((prev) => prev.filter((_, idx) => idx !== index));
    },
    [setCSVCollaboratorPreviewData]
  );

  const handleRemoveInvalidItem = useCallback(
    (index: number) => {
      setInvalidData((prev) => prev.filter((_, idx) => idx !== index));
    },
    [setInvalidData]
  );

  const handleEditInvalidItem = useCallback(
    (index: number) => {
      setEditingIndex(index);
      setEditingCollaborator({ ...invalidData[index] });
    },
    [invalidData]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingCollaborator(null);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingCollaborator !== null && editingIndex !== null) {
      const validatedCollaborator = validateCollaborator(editingCollaborator);
      if (validatedCollaborator.isValid) {
        setCSVCollaboratorPreviewData((prev) => [...prev, validatedCollaborator]);
        setInvalidData((prev) => prev.filter((_, idx) => idx !== editingIndex));
      } else {
        setInvalidData((prev) =>
          prev.map((item, idx) =>
            idx === editingIndex ? validatedCollaborator : item
          )
        );
      }
      setEditingIndex(null);
      setEditingCollaborator(null);
    }
  }, [
    editingCollaborator,
    editingIndex,
    validateCollaborator,
    setCSVCollaboratorPreviewData,
    setInvalidData,
  ]);

  const handleChangeEditingCollaborator = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setEditingCollaborator((prev) => (prev ? { ...prev, [name]: value } : prev));
    },
    []
  );

  const openErrorModal = useCallback(
    (
      errors: {
        collaborator: CSVCollaborator;
        error: string;
        is_restore?: boolean;
        id: string;
      }[]
    ) => {
      setErrorDetails((prevErrorDetails) => {
        const updatedErrorDetails: ErrorDetail[] = errors.map((error) => {
          const existingError = prevErrorDetails.find(
            (ed) => ed.id === error.id
          );
          return {
            ...error,
            restored: existingError ? existingError.restored : false,
          };
        });
        return updatedErrorDetails;
      });
      setShowErrorModal(true);
    },
    []
  );

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const downloadExampleCSV = () => {
    const exampleCSV =
      "Nome,E-mail\nExemplo1,Exemplo1@example.com\nExemplo2,Exemplo2@example.com";
    const blob = new Blob([exampleCSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-exemplo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Modal show onHide={onClose} size="xl" dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Importar Colaboradores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImportHistoryTable
            importHistory={importHistory}
            isFetchingHistory={isFetchingHistory}
            openErrorModal={openErrorModal}
          />
          <Form.Group>
            <Form.Label>
              Arquivo .csv <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-2 align-items-center mb-2">
              <Button
                variant="outline-secondary"
                onClick={() => document.getElementById("csvFileInput")?.click()}
              >
                Escolher arquivo
              </Button>
              {selectedFile && (
                <span className="text-muted">{selectedFile.name}</span>
              )}
              <Form.Control
                type="file"
                accept=".csv"
                key={fileInputKey}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.files && input.files[0]) {
                    handleCSVCollaboratorFileUpload(input.files[0]);
                  }
                }}
                style={{ display: "none" }}
                id="csvFileInput"
              />
            </div>
            <Form.Text className="text-muted">
              <strong>Padrão:</strong> O arquivo .csv não deve ter cabeçalho
              (nome das colunas). As colunas devem ser separadas por "," no
              seguinte formato:
              <br />
              Nome, E-mail
              <br />
              <Button variant="link" onClick={downloadExampleCSV}>
                Baixar modelo de exemplo
              </Button>
            </Form.Text>
          </Form.Group>

          {CSVCollaboratorError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erros encontrados:</strong>
              <pre className="mb-0 mt-2 small">{CSVCollaboratorError}</pre>
            </Alert>
          )}

          {processCSVCollaboratorError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erro no processamento:</strong>
              <pre className="mb-0 mt-2 small">
                {processCSVCollaboratorError.message}
              </pre>
            </Alert>
          )}

          <Form.Group className="mt-4">
            <Form.Label>Pesquisar colaboradores</Form.Label>
            <Form.Control
              type="text"
              placeholder="Pesquisar por nome ou email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>

          <CSVCollaboratorPreviewTable
            data={CSVCollaboratorPreviewData}
            searchTerm={searchTerm}
            handleRemovePreviewItem={handleRemovePreviewItem}
          />

          <InvalidDataTable
            data={invalidData}
            searchTerm={searchTerm}
            editingIndex={editingIndex}
            editingCollaborator={editingCollaborator}
            handleEditInvalidItem={handleEditInvalidItem}
            handleRemoveInvalidItem={handleRemoveInvalidItem}
            handleChangeEditingCollaborator={handleChangeEditingCollaborator}
            handleSaveEdit={handleSaveEdit}
            handleCancelEdit={handleCancelEdit}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleProcessImportInBackground}
            disabled={CSVCollaboratorPreviewData.length === 0 || isProcessingCSVCollaborator}
          >
            {isProcessingCSVCollaborator ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                IMPORTANDO
              </>
            ) : (
              "IMPORTAR"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ErrorDetailsModal
        show={showErrorModal}
        onHide={closeErrorModal}
        errorDetails={errorDetails}
        token={token}
        onCollaboratorRestored={handleCollaboratorRestored}
      />

      {processError && (
        <Alert
          variant="danger"
          className="mt-3"
          onClose={() => setProcessError(null)}
          dismissible
        >
          {processError}
        </Alert>
      )}
    </>
  );
}

export default CSVCollaboratorManagementView;
